import {
  OrderCancelAndRefundEligibility,
  OrderRefundMethodOption,
  OrderRefundOptionsItem,
  PaymentTransaction,
} from '@dapi/sdk';
import {
  amopPaidAmount,
  amopRefundAmounts,
  arnEwrRefundAmount,
  baseAmount,
  cardRefundAmount,
  cardRefundAmountWithObfees,
  oneMillionRupees,
  paidAmount,
  paidAmountWithMiles,
  paidAmountWithMilesWithoutCancellation,
  paidAmountWithOBFees,
  penaltyAmount,
  refundAmounts,
  refundAmountsWithMiles,
  refundAmountsWithMilesWithoutCancellation,
  usedAmount,
  usedAmountWithMiles,
  voucherPaidAmount,
  voucherPaidAmountWithMultipleVouchers,
  voucherPenaltyAmount,
  voucherRefundAmounts,
  voucherRefundAmountsWithMultipleVouchers,
  voucherWithCCPaidAmount,
  voucherWithCCPenaltyAmount,
  voucherWithCCRefundAmounts,
  zeroAmountInINR,
  zeroAmountInMYR,
  zeroBaseAmount,
} from './amount';
import {
  sekCurrency,
} from './currency';
import {
  alternativePaymentMethod,
  customPaymentMethod,
  paymentCard,
  payPalPayment,
  voucherPayment,
  voucherPayment2,
  voucherPayment3,
  voucherPaymentWithIdVoucher,
} from './payment';
import {
  paymentDetails,
} from './payment-transaction';
import {
  mockPenaltyDetail,
} from './penalty';
import {
  refundMethodOptionForMiles,
  refundMethodOptionForMilesWithoutCancellation,
  refundMethodOptions,
} from './refund-method';
import {
  traveler,
  traveler2,
  traveler3,
} from './traveler';
import {
  travelerDocumentsForMilesRefundWithTwoPax,
} from './traveler-documents';

/**
 * Generate refund method options by traveler document id
 *
 * @param travelDocumentId traveler document id
 * @param currency currency
 * @param paymentCardAmountValue payment card amount value
 * @param paypalAmountValue PayPal ampunt value
 * @param onlineBankingAmountValue Online banking amount value
 * @param unknownMethodAmountValue unknown method amount value
 */
export function refundMethodOptionsByTravelDocumentId(
  travelDocumentId: string,
  currency: string,
  paymentCardAmountValue: number,
  paypalAmountValue = 0,
  onlineBankingAmountValue = 0,
  unknownMethodAmountValue = 0
): OrderRefundMethodOption[] {
  const paymentDetailsArray: PaymentTransaction[] = [];

  if (paymentCardAmountValue) {
    paymentDetailsArray.push({
      paymentMethod: paymentCard,
      amount: {
        value: paymentCardAmountValue,
        currencyCode: currency,
        currency: sekCurrency
      }
    });
  }

  if (paypalAmountValue) {
    paymentDetailsArray.push({
      paymentMethod: payPalPayment,
      amount: {
        value: paypalAmountValue,
        currencyCode: currency,
        currency: sekCurrency
      }
    });
  }

  if (onlineBankingAmountValue) {
    paymentDetailsArray.push({
      paymentMethod: alternativePaymentMethod,
      amount: {
        value: onlineBankingAmountValue,
        currencyCode: currency,
        currency: sekCurrency
      }
    });
  }
  if (unknownMethodAmountValue) {
    paymentDetailsArray.push({
      paymentMethod: {
        id: 'UNKNOWN',
        paymentType: 'AlternativePaymentMethod'
      },
      amount: {
        value: unknownMethodAmountValue,
        currencyCode: currency,
        currency: sekCurrency
      }
    });
  }

  return ([{
    id: (travelDocumentId + '-DEFAULT'),
    paymentDetails: paymentDetailsArray
  }]);
}

const refundOption1: OrderRefundOptionsItem = {
  documentType: 'eticket',
  paidAmount: paidAmount,
  refundAmounts: refundAmounts,
  refundMethodOptions: [refundMethodOptions],
  refundType: 'involuntary',
  status: 'refundable',
  travelDocumentId: '6072160342328',
  travelerIds: ['TRAVELER-2-MOCK'],
  usedAmount: usedAmount
};

export const involuntaryRefundOption: OrderCancelAndRefundEligibility = {
  refundStatus: 'manuallyRefundable',
  refundOptions: [{
    ...refundOption1,
    paidAmount: undefined,
    refundAmounts: undefined,
    usedAmount: undefined,
    refundMethodOptions: undefined,
    travelDocumentId: '1052467303580',
    status: 'manuallyRefundable',
    travelerIds: [traveler.id]
  }]
};

export const refundOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    refundOption1,
    {
      ...refundOption1,
      travelDocumentId: '6072160342329',
      travelerIds: ['TRAVELER-1-MOCK'],
      refundMethodOptions: [{
        ...refundMethodOptions,
        id: '6072160342329-DEFAULT',
        paymentDetails: [{
          ...paymentDetails,
          amount: {
            ...baseAmount,
            value: 2795
          }
        }]
      }],
      refundAmounts: {
        ...refundAmounts,
        base: {
          ...baseAmount,
          value: 1830
        },
        total: {
          ...baseAmount,
          value: 2795
        },
        totalTaxes: {
          ...baseAmount,
          value: 965
        }
      },
      paidAmount: {
        ...paidAmount,
        base: {
          ...baseAmount,
          value: 1830
        }
      }
    },
    {
      ...refundOption1,
      travelDocumentId: '6072160342330',
      travelerIds: [traveler.id],
      refundMethodOptions: [{
        ...refundMethodOptions,
        id: '6072160342330-DEFAULT',
        paymentDetails: [{
          ...paymentDetails,
          amount: {
            ...baseAmount,
            value: 2795
          }
        }]
      }],
      refundAmounts: {
        ...refundAmounts,
        base: {
          ...baseAmount,
          value: 1830
        },
        total: {
          ...baseAmount,
          value: 2795
        },
        totalTaxes: {
          ...baseAmount,
          value: 965
        }
      },
      paidAmount: {
        ...paidAmount,
        base: {
          ...baseAmount,
          value: 1830
        }
      }
    }
  ]
};

export const arnEwrRefundOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    {
      travelDocumentId: '1172457148333',
      documentType: 'eticket',
      travelerIds: [traveler.id],
      status: 'refundable',
      refundAmounts: arnEwrRefundAmount,
      refundMethodOptions: refundMethodOptionsByTravelDocumentId('1172457148333', 'SEK', 14_361)
    },
    {
      travelDocumentId: '1172457148334',
      documentType: 'eticket',
      travelerIds: [traveler2.id],
      status: 'refundable',
      refundAmounts: arnEwrRefundAmount,
      refundMethodOptions: refundMethodOptionsByTravelDocumentId('1172457148334', 'SEK', 14_361)
    },
    {
      travelDocumentId: '1172457148335',
      documentType: 'eticket',
      travelerIds: [traveler3.id],
      status: 'refundable',
      refundAmounts: arnEwrRefundAmount,
      refundMethodOptions: refundMethodOptionsByTravelDocumentId('1172457148335', 'SEK', 11_213)
    }
  ]
};

export const amopRefundOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    {
      documentType: 'eticket',
      paidAmount: amopPaidAmount,
      refundAmounts: amopRefundAmounts,
      refundMethodOptions: [{
        ...refundMethodOptions,
        id: '1172457148333-DEFAULT',
        paymentDetails: [{
          ...paymentDetails,
          amount: {
            ...zeroAmountInMYR,
            value: 189_700
          },
          paymentMethod: payPalPayment
        }]
      }],
      travelDocumentId: '1172457148333',
      travelerIds: [traveler.id],
      status: 'refundable',
      usedAmount: usedAmount
    }
  ]
};

export const voucherRefundOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    documentType: 'eticket',
    paidAmount: voucherPaidAmount,
    penalty: voucherPenaltyAmount,
    refundAmounts: voucherRefundAmounts,
    refundMethodOptions: [
      {
        ...refundMethodOptions,
        id: '1172457148333-DEFAULT',
        paymentDetails: [{
          ...paymentDetails,
          amount: {
            ...zeroAmountInINR,
            value: 431_300
          },
          paymentMethod: voucherPayment
        }]
      },
      {
        ...refundMethodOptions,
        id: '0982167479759-EBKR',
        paymentDetails: [{
          ...paymentDetails,
          amount: {
            ...zeroAmountInINR,
            value: 431_300
          },
          paymentMethod: customPaymentMethod
        }]
      }
    ],
    travelDocumentId: '1172457148333',
    travelerIds: [traveler.id],
    status: 'refundable',
    usedAmount: {
      ...usedAmount,
      base: zeroAmountInINR
    }
  }
  ]
};

export const refundableWithMultipleVoucher: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    documentType: 'eticket',
    paidAmount: voucherPaidAmountWithMultipleVouchers,
    refundAmounts: voucherRefundAmountsWithMultipleVouchers,
    refundMethodOptions: [
      {
        ...refundMethodOptions,
        id: '1172457148333-DEFAULT',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: oneMillionRupees,
            paymentMethod: voucherPayment
          },
          {
            ...paymentDetails,
            amount: oneMillionRupees,
            paymentMethod: voucherPayment2
          },
          {
            ...paymentDetails,
            amount: {
              ...oneMillionRupees,
              value: 798_500
            },
            paymentMethod: voucherPayment3
          }
        ]
      },
      {
        ...refundMethodOptions,
        id: '0982167479759-EBKR',
        paymentDetails: [{
          ...paymentDetails,
          amount: {
            ...oneMillionRupees,
            value: 2_798_500
          },
          paymentMethod: customPaymentMethod
        }]
      }
    ],
    travelDocumentId: '1172457148333',
    travelerIds: [traveler.id],
    status: 'refundable',
    usedAmount: {
      ...usedAmount,
      base: {
        ...oneMillionRupees,
        value: 0
      }
    }
  }]
};

export const involuntaryRefundableWithMultipleVoucher: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    documentType: 'eticket',
    refundType: 'involuntary',
    paidAmount: voucherPaidAmountWithMultipleVouchers,
    refundAmounts: voucherRefundAmountsWithMultipleVouchers,
    refundMethodOptions: [
      {
        ...refundMethodOptions,
        id: '1172457148333-DEFAULT',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: oneMillionRupees,
            paymentMethod: voucherPayment
          },
          {
            ...paymentDetails,
            amount: oneMillionRupees,
            paymentMethod: voucherPayment2
          },
          {
            ...paymentDetails,
            amount: {
              ...oneMillionRupees,
              value: 798_500
            },
            paymentMethod: voucherPayment3
          }
        ]
      },
      {
        ...refundMethodOptions,
        id: '0982167479759-EBKR',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: {
              ...oneMillionRupees,
              value: 2_798_500
            },
            paymentMethod: customPaymentMethod
          }
        ]
      }
    ],
    travelDocumentId: '1172457148333',
    travelerIds: [traveler.id],
    status: 'refundable',
    usedAmount: {
      ...usedAmount,
      base: {
        ...oneMillionRupees
      }
    }
  }]
};

export const voucherWithCCRefundOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    documentType: 'eticket',
    paidAmount: voucherWithCCPaidAmount,
    penalty: voucherWithCCPenaltyAmount,
    refundAmounts: voucherWithCCRefundAmounts,
    refundMethodOptions: [
      {
        ...refundMethodOptions,
        id: '1172457148333-DEFAULT',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 1_537_000
            },
            paymentMethod: paymentCard
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 641_800
            },
            paymentMethod: voucherPayment
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 87_700
            },
            paymentMethod: voucherPayment2
          }
        ]
      },
      {
        ...refundMethodOptions,
        id: '0982167479759-VOUCHER',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 1_537_000
            },
            paymentMethod: voucherPaymentWithIdVoucher
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 11_600
            },
            paymentMethod: voucherPayment
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 11_600
            },
            paymentMethod: voucherPayment2
          }
        ]
      }],
    travelDocumentId: '1172457148333',
    travelerIds: [traveler.id],
    status: 'refundable',
    usedAmount: {
      ...usedAmount,
      base: zeroAmountInINR
    }
  }]
};

export const voucherWithCCInvoluntaryRefundOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    documentType: 'eticket',
    refundType: 'involuntary',
    paidAmount: voucherWithCCPaidAmount,
    penalty: voucherWithCCPenaltyAmount,
    refundAmounts: voucherWithCCRefundAmounts,
    refundMethodOptions: [
      {
        ...refundMethodOptions,
        id: '1172457148333-DEFAULT',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 1_537_000
            },
            paymentMethod: paymentCard
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 641_800
            },
            paymentMethod: voucherPayment
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 87_700
            },
            paymentMethod: voucherPayment2
          }
        ]
      },
      {
        ...refundMethodOptions,
        id: '0982167479759-VOUCHER',
        paymentDetails: [
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 1_537_000
            },
            paymentMethod: voucherPaymentWithIdVoucher
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 11_600
            },
            paymentMethod: voucherPayment
          },
          {
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 11_600
            },
            paymentMethod: voucherPayment2
          }
        ]
      }],
    travelDocumentId: '1172457148333',
    travelerIds: [traveler.id],
    status: 'refundable',
    usedAmount: {
      ...usedAmount,
      base: zeroAmountInINR
    }
  }]
};

export const voucherRefundTypeInvoluntaryOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    {
      documentType: 'eticket',
      paidAmount: voucherPaidAmount,
      penalty: voucherPenaltyAmount,
      refundType: 'involuntary',
      refundAmounts: voucherRefundAmounts,
      refundMethodOptions: [
        {
          ...refundMethodOptions,
          id: '1172457148333-DEFAULT',
          paymentDetails: [{
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 431_300
            },
            paymentMethod: voucherPayment
          }]
        },
        {
          ...refundMethodOptions,
          id: '0982167479759-EBKR',
          paymentDetails: [{
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 431_300
            },
            paymentMethod: customPaymentMethod
          }]
        }
      ],
      travelDocumentId: '1172457148333',
      travelerIds: [traveler.id],
      status: 'refundable',
      usedAmount: {
        ...usedAmount,
        base: zeroAmountInINR
      }
    }
  ]
};

const mockRefundOptionForMiles: OrderRefundOptionsItem = {
  travelDocumentId: '2202204070136',
  documentType: 'eticket',
  travelerIds: [traveler.id],
  status: 'refundable',
  refundAmounts: refundAmountsWithMiles,
  paidAmount: paidAmountWithMiles,
  usedAmount: usedAmountWithMiles,
  penalty: penaltyAmount,
  penaltyDetail: mockPenaltyDetail,
  refundMethodOptions: [refundMethodOptionForMiles]
};

export const cardRefundTypeInvoluntaryOptions: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    {
      documentType: 'eticket',
      refundAmounts: cardRefundAmount,
      refundMethodOptions: [
        {
          ...refundMethodOptions,
          id: '1172457148333-DEFAULT',
          paymentDetails: [{
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 14_361
            },
            paymentMethod: paymentCard
          }]
        }
      ],
      refundType: 'involuntary',
      status: 'refundable',
      travelDocumentId: '1172457148333',
      travelerIds: [traveler.id]
    },
    {
      documentType: 'eticket',
      refundAmounts: cardRefundAmount,
      refundMethodOptions: [
        {
          ...refundMethodOptions,
          id: '1172457148334-DEFAULT',
          paymentDetails: [{
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 14_361
            },
            paymentMethod: paymentCard
          }]
        }
      ],
      refundType: 'involuntary',
      status: 'refundable',
      travelDocumentId: '1172457148334',
      travelerIds: [traveler2.id]
    },
    {
      documentType: 'eticket',
      refundAmounts: {
        ...cardRefundAmount,
        base: {
          ...zeroAmountInINR,
          value: 9445
        },
        total: {
          ...zeroAmountInINR,
          value: 11_213
        },
        totalTaxes: {
          ...zeroAmountInINR,
          value: 1768
        }
      },
      refundMethodOptions: [
        {
          ...refundMethodOptions,
          id: '1172457148335-DEFAULT',
          paymentDetails: [{
            ...paymentDetails,
            amount: {
              ...zeroAmountInINR,
              value: 11_213
            },
            paymentMethod: paymentCard
          }]
        }
      ],
      refundType: 'involuntary',
      status: 'refundable',
      travelDocumentId: '1172457148335',
      travelerIds: [traveler3.id]
    }
  ]
};

export const cancelAndRefundForMiles: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [mockRefundOptionForMiles]
};

export const cancelAndRefundForMilesWithTwoPax: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    {
      ...mockRefundOptionForMiles,
      travelDocumentId: travelerDocumentsForMilesRefundWithTwoPax[0].id
    },
    {
      ...mockRefundOptionForMiles,
      travelDocumentId: travelerDocumentsForMilesRefundWithTwoPax[1].id,
      travelerIds: [traveler2.id]
    }
  ]
};

export const cancelAndRefundForMilesWithTwoPaxWithoutPenalty: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [
    {
      ...mockRefundOptionForMiles,
      travelDocumentId: travelerDocumentsForMilesRefundWithTwoPax[0].id,
      penalty: undefined,
      penaltyDetail: undefined
    },
    {
      ...mockRefundOptionForMiles,
      travelDocumentId: travelerDocumentsForMilesRefundWithTwoPax[1].id,
      travelerIds: [traveler2.id],
      penalty: undefined,
      penaltyDetail: undefined
    }
  ]
};

export const cancelAndRefundForMilesWithoutCancellation: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    travelDocumentId: '2202204070136',
    documentType: 'eticket',
    travelerIds: [traveler.id],
    status: 'refundable',
    refundAmounts: refundAmountsWithMilesWithoutCancellation,
    paidAmount: paidAmountWithMilesWithoutCancellation,
    usedAmount: usedAmountWithMiles,
    refundMethodOptions: [refundMethodOptionForMilesWithoutCancellation]
  }]
};

export const cancelAndRefundWithObFee: OrderCancelAndRefundEligibility = {
  refundStatus: 'refundable',
  refundOptions: [{
    travelDocumentId: '1052467303580',
    documentType: 'eticket',
    travelerIds: [traveler.id],
    status: 'refundable',
    usedAmount: zeroBaseAmount,
    refundType: 'involuntary',
    paidAmount: paidAmountWithOBFees,
    refundAmounts: cardRefundAmountWithObfees,
    refundMethodOptions: refundMethodOptionsByTravelDocumentId('1052467303580', 'EUR', 39_342)
  },
  {
    travelDocumentId: '1052467303579',
    documentType: 'eticket',
    travelerIds: [traveler2.id],
    status: 'refundable',
    usedAmount: zeroBaseAmount,
    refundType: 'involuntary',
    paidAmount: paidAmountWithOBFees,
    refundAmounts: cardRefundAmountWithObfees,
    refundMethodOptions: refundMethodOptionsByTravelDocumentId('1052467303579', 'EUR', 39_342)
  }]
};
